import { trackEmailSignup } from '@theme/gtm-events';

/**
 * Tracks email_signup events from:
 *   1. Klaviyo form submissions (the primary signup mechanism)
 *   2. Native Shopify newsletter (customer) forms as a fallback
 *
 * Contact / support forms that also POST to /contact are excluded.
 */

// ---------------------------------------------------------------------------
// Klaviyo: listens for the klaviyoForms global event
// ---------------------------------------------------------------------------

/**
 * Klaviyo's detail.$form is usually a jQuery object — unwrap to a DOM Element
 * before calling closest / querySelector.
 *
 * @param {unknown} $form
 * @returns {Element | null}
 */
function unwrapFormEl($form) {
  if (!$form) return null;
  if ($form instanceof Element) return $form;
  // jQuery / array-like
  if (typeof /** @type {{ get?: Function }} */ ($form).get === 'function') {
    const el = /** @type {{ get: (i: number) => unknown }} */ ($form).get(0);
    return el instanceof Element ? el : null;
  }
  if (typeof /** @type {{ jquery?: unknown }} */ ($form).jquery !== 'undefined') {
    const el = /** @type {unknown[]} */ ($form)[0];
    return el instanceof Element ? el : null;
  }
  return null;
}

function onKlaviyoSubmit(e) {
  const { formId, metaData, $form } = e.detail ?? {};
  if (!formId && !$form) return;

  const formEl =
    unwrapFormEl($form) ??
    document.querySelector(`[data-klaviyo-form-id="${formId}"]`) ??
    document.querySelector('.klaviyo-form');

  const source = deriveSignupSource(formEl);

  // Klaviyo stores the submitted email in metaData or in the form's input
  const emailInput = formEl?.querySelector('input[type="email"]');
  const rawEmail = metaData?.email ?? emailInput?.value ?? null;

  trackEmailSignup(source, rawEmail);
}

// Klaviyo v3 custom events
window.addEventListener('klaviyoForms', (e) => {
  if (/** @type {CustomEvent} */ (e).detail?.type === 'submit') {
    onKlaviyoSubmit(e);
  }
});

// ---------------------------------------------------------------------------
// Native Shopify newsletter forms (fallback for non-Klaviyo email blocks)
// ---------------------------------------------------------------------------

document.addEventListener('submit', (e) => {
  const form = /** @type {HTMLFormElement | null} */ (e.target);
  if (!(form instanceof HTMLFormElement)) return;

  const emailInput = form.querySelector('input[type="email"]');
  if (!emailInput) return;

  // Shopify injects form_type: "customer" for newsletter, "contact" for support forms.
  // Both often POST to /contact — only track newsletter / explicit subscribe.
  const formType = /** @type {HTMLInputElement | null} */ (
    form.querySelector('input[name="form_type"]')
  )?.value ?? '';
  const action = form.getAttribute('action') ?? '';
  const isNewsletter =
    formType === 'customer' ||
    form.classList.contains('email-signup__form') ||
    action.includes('subscribe');

  if (!isNewsletter) return;

  const source = deriveSignupSource(form);
  const rawEmail = /** @type {HTMLInputElement} */ (emailInput).value ?? null;

  trackEmailSignup(source, rawEmail);
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveSignupSource(el) {
  if (!el) return 'footer';
  if (el.closest('[data-signup-source]')) {
    return el.closest('[data-signup-source]').dataset.signupSource;
  }

  // Klaviyo / theme back-in-stock forms
  const bisHint = [
    el.id,
    el.className,
    el.getAttribute('data-testid'),
    el.getAttribute('aria-label'),
    el.querySelector?.('[name="email"]')?.getAttribute('placeholder'),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    bisHint.includes('back-in-stock') ||
    bisHint.includes('back_in_stock') ||
    bisHint.includes('bis-') ||
    bisHint.includes('notify') ||
    bisHint.includes('waitlist')
  ) {
    return 'back_in_stock';
  }

  if (el.closest('[data-gtm-nav="footer"]')) return 'footer';
  if (el.closest('dialog, .modal, .popup, [role="dialog"]')) return 'popup';
  return 'footer';
}
